import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/PartsManagement.css';

const PartsManagement = () => {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState({
    sku: '',
    nameHe: '',
    nameEn: '',
    cost: '',
    category: '',
    showInCalculator: false
  });

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const response = await api.get('/parts');
      setParts(response.data);
    } catch (error) {
      console.error('Error fetching parts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/parts?search=${search}`);
      setParts(response.data);
    } catch (error) {
      console.error('Error searching parts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPart) {
        await api.put(`/parts/${editingPart._id}`, formData);
      } else {
        await api.post('/parts', formData);
      }
      setShowModal(false);
      setEditingPart(null);
      setFormData({ sku: '', nameHe: '', nameEn: '', cost: '', category: '', showInCalculator: false });
      fetchParts();
    } catch (error) {
      alert(error.response?.data?.message || 'שגיאה בשמירה');
    }
  };

  const handleEdit = (part) => {
    setEditingPart(part);
    setFormData({
      sku: part.sku,
      nameHe: part.nameHe,
      nameEn: part.nameEn || '',
      cost: part.cost,
      category: part.category || '',
      showInCalculator: part.showInCalculator || false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם למחוק פריט זה?')) {
      try {
        await api.delete(`/parts/${id}`);
        fetchParts();
      } catch (error) {
        alert('שגיאה במחיקה');
      }
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const rows = text.split('\n').filter(row => row.trim());
        const parts = [];

        for (let i = 1; i < rows.length; i++) {
          const matches = rows[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
          if (matches && matches.length >= 7) {
            const sku = matches[1]?.replace(/"/g, '').trim();
            const nameEn = matches[2]?.replace(/"/g, '').trim();
            const nameHe = matches[3]?.replace(/"/g, '').trim();
            const costStr = matches[6]?.replace(/"/g, '').replace(/,/g, '').trim();
            const cost = parseFloat(costStr) || 0;

            if (sku) {
              parts.push({
                sku,
                nameHe: nameHe || nameEn || '',
                nameEn: nameEn || '',
                cost
              });
            }
          }
        }

        if (parts.length === 0) {
          alert('לא נמצאו פריטים בקובץ');
          return;
        }

        const response = await api.post('/parts/import', { parts });
        alert(response.data.message);
        fetchParts();
      } catch (error) {
        console.error('Import error:', error);
        alert('שגיאה בייבוא: ' + (error.response?.data?.message || error.message));
      }
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const filteredParts = parts.filter(part =>
    part.sku.includes(search) ||
    part.nameHe.includes(search) ||
    (part.nameEn && part.nameEn.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="parts-management">
      <div className="page-header">
        <h1>עריכת פריטים</h1>
      </div>

      <div className="toolbar">
        <div className="search-box">
          <input
            type="text"
            placeholder="חיפוש לפי מק״ט או שם..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="toolbar-buttons">
          <label className="btn-import">
            ייבוא מ-CSV
            <input type="file" accept=".csv" onChange={handleImportCSV} hidden />
          </label>
          <button className="btn-primary" onClick={() => {
            setEditingPart(null);
            setFormData({ sku: '', nameHe: '', nameEn: '', cost: '', category: '', showInCalculator: false });
            setShowModal(true);
          }}>
            + הוסף פריט
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">טוען...</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>מק״ט</th>
                <th>שם בעברית</th>
                <th>שם באנגלית</th>
                <th>עלות (₪)</th>
                <th>קטגוריה</th>
                <th>מחשבון</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filteredParts.map(part => (
                <tr key={part._id}>
                  <td>{part.sku}</td>
                  <td>{part.nameHe}</td>
                  <td>{part.nameEn}</td>
                  <td>{part.cost.toFixed(2)}</td>
                  <td>{part.category}</td>
                  <td>{part.showInCalculator ? '✓' : ''}</td>
                  <td className="actions">
                    <button className="btn-edit" onClick={() => handleEdit(part)}>ערוך</button>
                    <button className="btn-delete" onClick={() => handleDelete(part._id)}>מחק</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredParts.length === 0 && (
            <div className="no-data">לא נמצאו פריטים</div>
          )}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingPart ? 'עריכת פריט' : 'הוספת פריט חדש'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>מק״ט</label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  required
                  disabled={editingPart}
                />
              </div>
              <div className="form-group">
                <label>שם בעברית</label>
                <input
                  type="text"
                  value={formData.nameHe}
                  onChange={(e) => setFormData({ ...formData, nameHe: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>שם באנגלית</label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>עלות (₪)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cost}
                  onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>קטגוריה</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.showInCalculator}
                    onChange={(e) => setFormData({ ...formData, showInCalculator: e.target.checked })}
                  />
                  הצג במחשבון פריטים
                </label>
              </div>
              <div className="modal-buttons">
                <button type="submit" className="btn-primary">שמור</button>
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>ביטול</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartsManagement;